import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import PrimaryNav from 'components/NavigationBar';
import Header from 'components/LeftMenu/Header';
import MulticastFeeds from 'components/LeftMenu/MulticastFeeds/container';
import UnicastFeeds from 'components/LeftMenu/UnicastFeeds/container';
import Rooms from 'components/LeftMenu/Rooms/container';
import Icon from 'components/Shared/Common/Icon';
import { injectIntl } from 'react-intl';
import NotificationBlock from 'components/Shared/Blocks/NotificationBlock';
import { QualityNetworkStyled, Styled, ContentChannelStyled } from 'components/LeftMenu/style';
import useNetworkState from 'hooks/useNetworkState';
import { Modal } from 'antd';
import { selectors } from '@amplement/backend-connector';

const NetworkErrorComponent = ({ intl }) => {
    const { error } = useNetworkState(1000);

    if (error) {
        return (
            <QualityNetworkStyled
                className="s-quality-network active"
            >
                <Icon iconName="CloudOff" />
                <div className="text">
                    {error && intl.formatMessage({ id: error })}
                </div>
            </QualityNetworkStyled>
        );
    }
    return null;
}

NetworkErrorComponent.propTypes = {
    intl: PropTypes.any.isRequired,
};

const NetworkError = memo(injectIntl(NetworkErrorComponent));

/*
    to simulate action with Ws reconnecion fail
    {
        type: 'WS_RECONNECT_FAILED',
        payload: { message: 'Unable to recover' }
    }
*/
const NetworkModalError = injectIntl(memo(({ intl }) => {
    const error = useSelector(selectors.network.getWsErrorSelector);
    const [modal, contextHolder] = Modal.useModal();
    const modalInstance = useRef();

    useEffect(() => {
        if (error === 'Unable to recover') {
            modalInstance.current = modal.error({
                title: intl.formatMessage({ id: 'global.websocket.status.ended' }),
                onOk: () => window.location.reload(),
                okText: intl.formatMessage({ id: 'global.button.reload' }),
                closeIcon: false,
            });
        } else if (!error && modalInstance.current) {
            modalInstance.current.destroy();
            modalInstance.current = null;
        }

        return () => {
            if (modalInstance.current) {
                modalInstance.current.destroy();
                modalInstance.current = null;
            }
        };
    }, [error, modal, intl]);

    return contextHolder;
}));

const SecondaryNav = memo(({ _currentFeed }) => (
    <Styled className="ui-secondary-nav" key="nav-2">
        <Header />
        <ContentChannelStyled className="content-channel-styled">
            <NotificationBlock />
            <Rooms _currentFeed={_currentFeed} />
            <MulticastFeeds _currentFeed={_currentFeed} />
            <UnicastFeeds _currentFeed={_currentFeed} />
        </ContentChannelStyled>
        <NetworkError />
        <NetworkModalError />
    </Styled>
));

SecondaryNav.propTypes = {
    _currentFeed: PropTypes.string
};

const LeftMenu  = memo(({
    _currentFeed,
    currentPath,
    _me,
    hasPrimaryNav = true,
    hasSecondaryNav = true
}) => (
    <>
        {hasPrimaryNav && (
            <PrimaryNav 
                key="nav-1" 
                currentPath={currentPath} 
                _me={_me} 
            />
        )}
        {hasSecondaryNav && <SecondaryNav _currentFeed={_currentFeed} />}
    </>
));

LeftMenu.propTypes = {
    _me: PropTypes.string,
    _currentFeed: PropTypes.string,
    currentPath: PropTypes.string,
    hasPrimaryNav: PropTypes.bool,
    hasSecondaryNav: PropTypes.bool
};

export default LeftMenu;
